import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  RiSearchLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCalendarLine,
  RiTimeLine,
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiCheckLine,
  RiCloseLine,
  RiCake2Line,
  RiShieldCheckLine,
  RiFileTextLine,
  RiRefreshLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiMoneyDollarCircleLine,
  RiLayoutGridLine,
  RiListCheck,
  RiSparklingLine,
  RiAlertLine,
  RiExchangeDollarLine,
  RiCoinsLine,
} from "react-icons/ri";
import {
  Users,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Clock3,
  FileSignature,
  DollarSign,
  Trophy,
  Utensils,
  PlusCircle,
  CreditCard,
  Receipt,
  Wallet,
} from "lucide-react";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import ModalWrapper from "../../components/common/ModalWrapper";

// Squad size pricing calculation helper
export const calculateSquadPricing = (packageName = "", participantsCount = 10, savedTotal = null) => {
  const count = Number(participantsCount) || 10;
  const name = String(packageName || "").toLowerCase();

  let basePrice = 375;
  let basePlayers = 15;
  let extraPlayerRate = 25;
  let tierLabel = "All-Star Squad";

  if (name.includes("small")) {
    basePrice = 275;
    basePlayers = 10;
    extraPlayerRate = 20;
    tierLabel = "Small Squad";
  } else if (name.includes("all-star") || name.includes("all star") || name.includes("allstar")) {
    basePrice = 375;
    basePlayers = 15;
    extraPlayerRate = 25;
    tierLabel = "All-Star Squad";
  } else if (name.includes("big")) {
    basePrice = 475;
    basePlayers = 20;
    extraPlayerRate = 30;
    tierLabel = "Big League";
  } else if (name.includes("large") || name.includes("25")) {
    basePrice = 550;
    basePlayers = 25;
    extraPlayerRate = 35;
    tierLabel = "Large Group (25+)";
  }

  const extraPlayers = Math.max(0, count - basePlayers);
  const extraCost = extraPlayers * extraPlayerRate;
  const calculatedTotal = basePrice + extraCost;
  const finalTotal = savedTotal != null && Number(savedTotal) > 0 ? Number(savedTotal) : calculatedTotal;

  return {
    tierLabel,
    basePrice,
    basePlayers,
    extraPlayers,
    extraPlayerRate,
    extraCost,
    calculatedTotal,
    finalTotal,
  };
};

// Payment badge helper
export const getPaymentBadge = (paymentStatus = "unpaid") => {
  switch (paymentStatus) {
    case "paid":
      return {
        label: "Paid in Full",
        bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
        icon: <CheckCircle2 size={13} className="text-emerald-600" />,
      };
    case "deposit_paid":
      return {
        label: "Deposit Paid",
        bg: "bg-purple-50 text-purple-700 border-purple-200",
        dot: "bg-purple-500",
        icon: <DollarSign size={13} className="text-purple-600" />,
      };
    case "refunded":
      return {
        label: "Refunded",
        bg: "bg-gray-100 text-gray-700 border-gray-200",
        dot: "bg-gray-500",
        icon: <AlertCircle size={13} className="text-gray-500" />,
      };
    case "unpaid":
    default:
      return {
        label: "Unpaid",
        bg: "bg-rose-50 text-rose-700 border-rose-200",
        dot: "bg-rose-500 animate-pulse",
        icon: <AlertCircle size={13} className="text-rose-600" />,
      };
  }
};

// Booking status badge helper
export const getBookingStatusBadge = (status = "pending") => {
  switch (status) {
    case "confirmed":
      return {
        label: "Confirmed",
        bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
        icon: <CheckCircle2 size={13} className="text-emerald-600" />,
      };
    case "contacted":
      return {
        label: "In Review / Contacted",
        bg: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
        icon: <Clock3 size={13} className="text-blue-600" />,
      };
    case "completed":
      return {
        label: "Completed",
        bg: "bg-teal-50 text-teal-700 border-teal-200",
        dot: "bg-teal-500",
        icon: <CheckCircle2 size={13} className="text-teal-600" />,
      };
    case "cancelled":
      return {
        label: "Cancelled",
        bg: "bg-red-50 text-red-700 border-red-200",
        dot: "bg-red-500",
        icon: <AlertCircle size={13} className="text-red-600" />,
      };
    case "pending":
    default:
      return {
        label: "Pending Review",
        bg: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500 animate-pulse",
        icon: <Clock size={13} className="text-amber-600" />,
      };
  }
};

// Initial realistic mock requests matching the strikes-shots PartyBooking schema
const INITIAL_PARTY_REQUESTS = [
  {
    _id: "req_001",
    bookingReference: "SS-PR-9041",
    signeeType: "adult_with_dependants",
    attendanceOption: "both",
    status: "pending",
    paymentStatus: "unpaid",
    depositAmount: 0,
    totalAmount: 375,
    adminNotes: "Client requested confirmation on whether outside birthday cake is permitted. Staff to reply via email.",
    primarySignee: {
      firstName: "Sarah",
      lastName: "Jenkins",
      email: "sarah.jenkins@gmail.com",
      mobilePhone: "(404) 555-0192",
      dob: "1988-04-12",
      addressLine1: "742 Evergreen Terrace",
      addressLine2: "Apt 4B",
      city: "Atlanta",
      state: "GA",
      zipCode: "30301",
    },
    dependants: [
      {
        firstName: "Lucas",
        lastName: "Jenkins",
        dob: "2015-06-18",
        mobilePhone: "",
        city: "Atlanta",
        state: "GA",
      },
      {
        firstName: "Emma",
        lastName: "Jenkins",
        dob: "2018-09-03",
        mobilePhone: "",
        city: "Atlanta",
        state: "GA",
      },
    ],
    packageDetails: {
      packageName: "All-Star Squad Party",
      sport: "Indoor Soccer",
      eventDate: "2026-09-26",
      timeSlot: "1:00 PM - 3:30 PM",
      participantsCount: 15,
      celebrantName: "Lucas Jenkins",
      celebrantAge: "11",
      addons: ["Additional Sport Split (45m Soccer + 45m Basketball)", "Dedicated Referee"],
      foodSelections: ["3x Large Artisan Pepperoni Pizzas", "Hydration Soda & Juice Station"],
      notes: "Lucas is turning 11! He loves soccer and basketball. We would love the private party bay decorated with blue and red balloons if permitted.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M12 40 C 30 10, 60 50, 90 25 S 130 60, 160 30",
      signedAt: "2026-09-08T14:32:00.000Z",
      ipAddress: "73.181.42.109",
    },
    createdAt: "2026-09-08T14:32:00.000Z",
  },
  {
    _id: "req_002",
    bookingReference: "SS-PR-9042",
    signeeType: "adult_with_dependants",
    attendanceOption: "dependants_only",
    status: "confirmed",
    paymentStatus: "deposit_paid",
    depositAmount: 150,
    totalAmount: 475,
    adminNotes: "Deposit of $150 received via Stripe. Court #2 and Party Bay A reserved.",
    primarySignee: {
      firstName: "Marcus",
      lastName: "Vance",
      email: "m.vance@techcorp.io",
      mobilePhone: "(678) 555-8321",
      dob: "1982-11-20",
      addressLine1: "1200 Peachtree St NE",
      addressLine2: "Suite 150",
      city: "Atlanta",
      state: "GA",
      zipCode: "30309",
    },
    dependants: [
      {
        firstName: "Tyler",
        lastName: "Vance",
        dob: "2013-03-14",
        mobilePhone: "",
        city: "Atlanta",
        state: "GA",
      },
    ],
    packageDetails: {
      packageName: "Big League Party",
      sport: "Basketball",
      eventDate: "2026-10-03",
      timeSlot: "4:00 PM - 6:30 PM",
      participantsCount: 20,
      celebrantName: "Tyler Vance",
      celebrantAge: "13",
      addons: ["Extended Play Time (+30 min)", "Trophy & Medal Presentation Package"],
      foodSelections: ["5x Fresh Artisan Pizzas", "Crispy Finger Food Shareables", "Hydration Soda & Juice Station"],
      notes: "Middle school basketball team celebration. High energy squad! Please ensure basketballs are properly inflated.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M10 35 Q 45 10 70 30 T 130 25",
      signedAt: "2026-09-07T09:15:00.000Z",
      ipAddress: "68.205.112.44",
    },
    createdAt: "2026-09-07T09:15:00.000Z",
  },
  {
    _id: "req_003",
    bookingReference: "SS-PR-9043",
    signeeType: "adult",
    attendanceOption: "self",
    status: "contacted",
    paymentStatus: "unpaid",
    depositAmount: 0,
    totalAmount: 275,
    adminNotes: "Left voicemail and emailed to confirm preferred alternate time if 10:30 AM is taken.",
    primarySignee: {
      firstName: "David",
      lastName: "Kim",
      email: "david.kim@apexengineering.com",
      mobilePhone: "(470) 555-4912",
      dob: "1994-08-05",
      addressLine1: "345 North Highland Ave",
      addressLine2: "",
      city: "Atlanta",
      state: "GA",
      zipCode: "30307",
    },
    dependants: [],
    packageDetails: {
      packageName: "Small Squad Party",
      sport: "Indoor Soccer",
      eventDate: "2026-09-20",
      timeSlot: "10:30 AM - 1:00 PM",
      participantsCount: 10,
      celebrantName: "David Kim",
      celebrantAge: "32",
      addons: [],
      foodSelections: ["2x Artisan Margherita Pizzas"],
      notes: "Casual 5v5 soccer matchup with college alumni friends followed by lunch.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M15 45 C 50 15, 80 40, 140 20",
      signedAt: "2026-09-06T18:40:00.000Z",
      ipAddress: "99.123.88.19",
    },
    createdAt: "2026-09-06T18:40:00.000Z",
  },
  {
    _id: "req_004",
    bookingReference: "SS-PR-9044",
    signeeType: "adult_with_dependants",
    attendanceOption: "both",
    status: "confirmed",
    paymentStatus: "paid",
    depositAmount: 375,
    totalAmount: 375,
    adminNotes: "Paid in full via online card processing. All-Star court #1 confirmed.",
    primarySignee: {
      firstName: "Rachel",
      lastName: "Morris",
      email: "rachel.morris77@yahoo.com",
      mobilePhone: "(770) 555-6672",
      dob: "1986-02-28",
      addressLine1: "880 Marietta Hwy",
      addressLine2: "",
      city: "Roswell",
      state: "GA",
      zipCode: "30075",
    },
    dependants: [
      {
        firstName: "Chloe",
        lastName: "Morris",
        dob: "2016-10-10",
        mobilePhone: "",
        city: "Roswell",
        state: "GA",
      },
    ],
    packageDetails: {
      packageName: "All-Star Squad Party",
      sport: "Volleyball",
      eventDate: "2026-10-10",
      timeSlot: "1:00 PM - 3:30 PM",
      participantsCount: 14,
      celebrantName: "Chloe Morris",
      celebrantAge: "10",
      addons: ["Tournament Referee / Coordinator"],
      foodSelections: ["3x Artisan Cheese Pizzas", "Hydration Soda & Juice Station"],
      notes: "Chloe's 10th birthday! Beginner volleyball party with coaches assisting.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M10 30 Q 60 5 110 35 T 160 20",
      signedAt: "2026-09-05T11:20:00.000Z",
      ipAddress: "24.199.64.12",
    },
    createdAt: "2026-09-05T11:20:00.000Z",
  },
  {
    _id: "req_005",
    bookingReference: "SS-PR-9045",
    signeeType: "adult",
    attendanceOption: "self",
    status: "pending",
    paymentStatus: "unpaid",
    depositAmount: 0,
    totalAmount: 655,
    adminNotes: "Large group inquiry for 28 players. Calculated based on 25 base + 3 extra players ($35/ea).",
    primarySignee: {
      firstName: "Anthony",
      lastName: "Rossi",
      email: "anthony@rossicapital.com",
      mobilePhone: "(404) 555-9018",
      dob: "1980-05-15",
      addressLine1: "3340 Peachtree Rd NE",
      addressLine2: "Suite 1800",
      city: "Atlanta",
      state: "GA",
      zipCode: "30326",
    },
    dependants: [],
    packageDetails: {
      packageName: "Large Group (25+)",
      sport: "Indoor Cricket",
      eventDate: "2026-10-17",
      timeSlot: "7:00 PM - 9:30 PM",
      participantsCount: 28,
      celebrantName: "Annual Firm Tournament",
      celebrantAge: "Corporate",
      addons: ["Additional Sport Split", "Custom Music Playlist & PA System", "Dedicated Referee"],
      foodSelections: ["6x Artisan Pizzas", "Premium Finger Food Shareables", "Hydration Soda & Juice Station"],
      notes: "Corporate team building tournament. Needs full court access and tournament bracket coordinator.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M12 50 C 40 20, 80 50, 150 15",
      signedAt: "2026-09-04T16:50:00.000Z",
      ipAddress: "172.56.21.89",
    },
    createdAt: "2026-09-04T16:50:00.000Z",
  },
  {
    _id: "req_006",
    bookingReference: "SS-PR-9046",
    signeeType: "adult_with_dependants",
    attendanceOption: "both",
    status: "cancelled",
    paymentStatus: "unpaid",
    depositAmount: 0,
    totalAmount: 275,
    adminNotes: "Cancelled by customer due to schedule conflict with soccer tournament. Friendly follow-up sent.",
    primarySignee: {
      firstName: "Jessica",
      lastName: "Taylor",
      email: "jtaylor.designs@gmail.com",
      mobilePhone: "(678) 555-2244",
      dob: "1991-12-04",
      addressLine1: "512 Glenwood Ave",
      addressLine2: "",
      city: "Atlanta",
      state: "GA",
      zipCode: "30312",
    },
    dependants: [
      {
        firstName: "Noah",
        lastName: "Taylor",
        dob: "2017-08-22",
        mobilePhone: "",
        city: "Atlanta",
        state: "GA",
      },
    ],
    packageDetails: {
      packageName: "Small Squad Party",
      sport: "Indoor Soccer",
      eventDate: "2026-09-19",
      timeSlot: "1:00 PM - 3:30 PM",
      participantsCount: 10,
      celebrantName: "Noah Taylor",
      celebrantAge: "9",
      addons: [],
      foodSelections: ["2x Artisan Pepperoni Pizzas"],
      notes: "Wanted outdoor view if possible. Later rescheduled.",
    },
    legalConsent: {
      agreedTerms: true,
      agreedWaiver: true,
      electronicConsent: true,
      signatureData: "M10 40 Q 50 10 100 40 T 150 30",
      signedAt: "2026-09-03T10:10:00.000Z",
      ipAddress: "76.111.92.5",
    },
    createdAt: "2026-09-03T10:10:00.000Z",
  },
];

const AdminPartyRequests = () => {
  const API = process.env.REACT_APP_API_URL;

  // Requests state initialized with rich mock data
  const [requests, setRequests] = useState(INITIAL_PARTY_REQUESTS);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" | "grid"

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [packageFilter, setPackageFilter] = useState("all");
  const [sportFilter, setSportFilter] = useState("all");

  // Detail Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Status & Payment Edit Modal state
  const [editingRequest, setEditingRequest] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: "pending",
    paymentStatus: "unpaid",
    depositAmount: 0,
    totalAmount: 0,
    adminNotes: "",
  });
  const [savingStatus, setSavingStatus] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  // Fetch from API if backend is ready
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/party-request/all`);
      if (res.data?.success && Array.isArray(res.data.bookings) && res.data.bookings.length > 0) {
        // Ensure every booking has calculated total & default unpaid paymentStatus if missing
        const formatted = res.data.bookings.map((b) => {
          const pricing = calculateSquadPricing(
            b.packageDetails?.packageName,
            b.packageDetails?.participantsCount,
            b.totalAmount
          );
          return {
            ...b,
            paymentStatus: b.paymentStatus || (b.depositAmount > 0 ? "deposit_paid" : "unpaid"),
            totalAmount: b.totalAmount > 0 ? b.totalAmount : pricing.finalTotal,
          };
        });
        setRequests(formatted);
      }
    } catch (err) {
      console.log("Using local party requests UI dataset:", err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Filtered requests computation
  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      const customerName = `${req.primarySignee?.firstName || ""} ${req.primarySignee?.lastName || ""}`.toLowerCase();
      const celebrant = (req.packageDetails?.celebrantName || "").toLowerCase();
      const ref = (req.bookingReference || "").toLowerCase();
      const email = (req.primarySignee?.email || "").toLowerCase();
      const phone = (req.primarySignee?.mobilePhone || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();

      const matchesSearch =
        !q ||
        customerName.includes(q) ||
        celebrant.includes(q) ||
        ref.includes(q) ||
        email.includes(q) ||
        phone.includes(q);

      const matchesStatus = statusFilter === "all" || req.status === statusFilter;

      const currentPay = req.paymentStatus || "unpaid";
      const matchesPayment = paymentFilter === "all" || currentPay === paymentFilter;

      const matchesPackage =
        packageFilter === "all" ||
        (req.packageDetails?.packageName || "").toLowerCase().includes(packageFilter.toLowerCase());

      const matchesSport =
        sportFilter === "all" ||
        (req.packageDetails?.sport || "").toLowerCase().includes(sportFilter.toLowerCase());

      return matchesSearch && matchesStatus && matchesPayment && matchesPackage && matchesSport;
    });
  }, [requests, searchQuery, statusFilter, paymentFilter, packageFilter, sportFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage) || 1;
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRequests, currentPage, itemsPerPage]);

  // Handle open view details modal
  const handleOpenDetailModal = (req) => {
    setSelectedRequest(req);
    setIsDetailModalOpen(true);
  };

  // Handle open edit status modal
  const handleOpenEditModal = (req) => {
    setEditingRequest(req);
    const pricing = calculateSquadPricing(
      req.packageDetails?.packageName,
      req.packageDetails?.participantsCount,
      req.totalAmount
    );

    setStatusForm({
      status: req.status || "pending",
      paymentStatus: req.paymentStatus || "unpaid",
      depositAmount: req.depositAmount != null ? req.depositAmount : 0,
      totalAmount: req.totalAmount != null && req.totalAmount > 0 ? req.totalAmount : pricing.finalTotal,
      adminNotes: req.adminNotes || "",
    });
    setIsEditModalOpen(true);
  };

  // Save status & payment changes
  const handleSaveStatus = async (e) => {
    e.preventDefault();
    if (!editingRequest) return;

    try {
      setSavingStatus(true);
      const payload = {
        status: statusForm.status,
        paymentStatus: statusForm.paymentStatus,
        depositAmount: Number(statusForm.depositAmount),
        totalAmount: Number(statusForm.totalAmount),
        adminNotes: statusForm.adminNotes,
      };

      try {
        await axios.put(`${API}/party-request/${editingRequest._id}/status`, payload);
      } catch (err) {
        console.log("Mock update mode active:", err?.message);
      }

      // Update local state
      setRequests((prev) =>
        prev.map((r) =>
          r._id === editingRequest._id
            ? {
                ...r,
                ...payload,
              }
            : r
        )
      );

      // If viewing detail modal for the same request, update it too
      if (selectedRequest && selectedRequest._id === editingRequest._id) {
        setSelectedRequest((prev) => ({
          ...prev,
          ...payload,
        }));
      }

      setIsEditModalOpen(false);
      toast.success(
        `Booking ${editingRequest.bookingReference} updated to ${statusForm.paymentStatus.toUpperCase()} / ${statusForm.status.toUpperCase()}!`
      );
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  // Quick cycle payment status right from table
  const handleQuickCyclePayment = async (req, e) => {
    e.stopPropagation();
    const cycle = ["unpaid", "deposit_paid", "paid"];
    const current = req.paymentStatus || "unpaid";
    const nextIdx = (cycle.indexOf(current) + 1) % cycle.length;
    const nextPayment = cycle[nextIdx];

    const pricing = calculateSquadPricing(
      req.packageDetails?.packageName,
      req.packageDetails?.participantsCount,
      req.totalAmount
    );
    const total = req.totalAmount > 0 ? req.totalAmount : pricing.finalTotal;
    const newDeposit = nextPayment === "paid" ? total : nextPayment === "deposit_paid" ? Math.round(total / 2) : 0;

    try {
      try {
        await axios.put(`${API}/party-request/${req._id}/status`, {
          paymentStatus: nextPayment,
          depositAmount: newDeposit,
        });
      } catch (err) {
        // mock mode
      }

      setRequests((prev) =>
        prev.map((r) =>
          r._id === req._id
            ? {
                ...r,
                paymentStatus: nextPayment,
                depositAmount: newDeposit,
              }
            : r
        )
      );
      toast.success(`Payment updated to ${nextPayment.replace("_", " ").toUpperCase()}`);
    } catch (err) {
      toast.error("Failed to change payment status");
    }
  };

  // Delete booking request with confirmation
  const handleDeleteRequest = async (id, ref) => {
    const result = await Swal.fire({
      title: "Delete Party Request?",
      text: `Are you sure you want to delete ${ref || "this booking"}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CD1B1B",
      cancelButtonColor: "#4B5563",
      confirmButtonText: "Yes, delete request",
      cancelButtonText: "Cancel",
      background: "#fff",
      borderRadius: "1rem",
    });

    if (result.isConfirmed) {
      try {
        try {
          await axios.delete(`${API}/party-request/${id}`);
        } catch (err) {
          console.log("Mock deletion mode active:", err?.message);
        }

        setRequests((prev) => prev.filter((r) => r._id !== id));
        if (selectedRequest && selectedRequest._id === id) {
          setIsDetailModalOpen(false);
        }

        Swal.fire({
          title: "Deleted!",
          text: `Booking request ${ref || ""} has been deleted.`,
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        toast.error("Error deleting request");
      }
    }
  };

  // Package badge color helper
  const getPackageBadgeColor = (pkgName = "") => {
    const lower = pkgName.toLowerCase();
    if (lower.includes("small")) return "bg-sky-50 text-sky-700 border-sky-200";
    if (lower.includes("all-star")) return "bg-red-50 text-red-700 border-red-200";
    if (lower.includes("big")) return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-amber-50 text-amber-700 border-amber-200";
  };

  // KPI Calculations
  const totalCount = requests.length;
  const unpaidCount = requests.filter((r) => !r.paymentStatus || r.paymentStatus === "unpaid").length;
  const paidOrDepositCount = requests.filter((r) => r.paymentStatus === "deposit_paid" || r.paymentStatus === "paid").length;
  const totalExpectedRevenue = requests
    .filter((r) => r.status !== "cancelled")
    .reduce((sum, r) => {
      const p = calculateSquadPricing(r.packageDetails?.packageName, r.packageDetails?.participantsCount, r.totalAmount);
      return sum + p.finalTotal;
    }, 0);

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-6 w-full font-body select-none">
      {/* 1. Header Section */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
              Party Booking Requests
            </h1>
            <p className="text-secondaryTextColor text-sm mt-1 font-subheading">
              Track squad size pricing, initial unpaid submissions, payment receipts, and schedule confirmations.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRequests}
              disabled={loading}
              className="bg-white border border-gray-200 text-gray-700 hover:text-primaryBtnHoverColor hover:border-gray-300 py-2.5 px-3.5 rounded-xl flex items-center gap-1.5 shadow-sm text-xs font-semibold cursor-pointer transition-all duration-200 active:scale-95"
              title="Refresh requests list"
            >
              <RiRefreshLine size={16} className={loading ? "animate-spin text-primaryBtnHoverColor" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 shadow-inner">
              <button
                onClick={() => setViewMode("table")}
                className={`p-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
                  viewMode === "table"
                    ? "bg-white text-primaryBtnColor shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                title="Table View"
              >
                <RiListCheck size={16} />
                <span className="hidden md:inline">Table</span>
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-primaryBtnColor shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                title="Card Grid View"
              >
                <RiLayoutGridLine size={16} />
                <span className="hidden md:inline">Cards</span>
              </button>
            </div>
          </div>
        </div>

        {/* 2. Four KPI Summary Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
          {/* Card 1: Total Parties */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block font-subheading">
                Total Requests
              </span>
              <div className="text-2xl font-black text-primaryTextColor font-heading mt-1">
                {totalCount} Parties
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-1 flex items-center gap-1">
                All submitted party bookings
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shadow-inner">
              <RiCake2Line size={24} />
            </div>
          </div>

          {/* Card 2: Initially Unpaid */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block font-subheading">
                Payment Pending
              </span>
              <div className="text-2xl font-black text-rose-600 font-heading mt-1">
                {unpaidCount} Unpaid
              </div>
              <span className="text-[11px] text-rose-600 font-semibold mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> Initial status — awaiting deposit
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl shadow-inner">
              <CreditCard size={24} />
            </div>
          </div>

          {/* Card 3: Deposit / Paid in Full */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block font-subheading">
                Deposits & Paid
              </span>
              <div className="text-2xl font-black text-emerald-600 font-heading mt-1">
                {paidOrDepositCount} Secured
              </div>
              <span className="text-[11px] text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                <RiCheckLine /> Deposit received / Paid
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl shadow-inner">
              <CheckCircle2 size={24} />
            </div>
          </div>

          {/* Card 4: Squad Revenue Pipeline */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block font-subheading">
                Squad Value
              </span>
              <div className="text-2xl font-black text-primaryBtnHoverColor font-heading mt-1">
                ${totalExpectedRevenue.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-1 flex items-center gap-1">
                <RiMoneyDollarCircleLine size={13} /> Based on tier & squad size
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-red-50 text-primaryBtnHoverColor flex items-center justify-center text-xl shadow-inner">
              <DollarSign size={24} />
            </div>
          </div>
        </div>

        {/* 3. Search & Multi-Filter Bar */}
        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
            <input
              type="text"
              placeholder="Search by customer, email, phone, reference #, or celebrant..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-xs sm:text-sm font-subheading text-primaryTextColor placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30 focus:border-primaryBtnHoverColor transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <RiCloseLine size={18} />
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Payment Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:inline">
                Payment:
              </span>
              <select
                value={paymentFilter}
                onChange={(e) => {
                  setPaymentFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="py-2 px-3 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/20 transition-all cursor-pointer"
              >
                <option value="all">All Payments</option>
                <option value="unpaid">Unpaid ({unpaidCount})</option>
                <option value="deposit_paid">Deposit Paid</option>
                <option value="paid">Paid in Full</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            {/* Booking Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:inline">
                Status:
              </span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="py-2 px-3 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/20 transition-all cursor-pointer"
              >
                <option value="all">All Statuses ({requests.length})</option>
                <option value="pending">Pending ({requests.filter((r) => r.status === "pending").length})</option>
                <option value="contacted">In Review / Contacted</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Package Filter */}
            <div className="flex items-center gap-1.5">
              <select
                value={packageFilter}
                onChange={(e) => {
                  setPackageFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="py-2 px-3 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/20 transition-all cursor-pointer"
              >
                <option value="all">All Packages</option>
                <option value="small">Small Squad (Up to 10)</option>
                <option value="all-star">All-Star Squad (Up to 15)</option>
                <option value="big">Big League (Up to 20)</option>
                <option value="large">Large Group (25+)</option>
              </select>
            </div>

            {/* Reset Filter Button */}
            {(searchQuery || statusFilter !== "all" || paymentFilter !== "all" || packageFilter !== "all" || sportFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setPaymentFilter("all");
                  setPackageFilter("all");
                  setSportFilter("all");
                  setCurrentPage(1);
                }}
                className="text-xs text-primaryBtnHoverColor hover:underline font-semibold px-2 py-1"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 4. Main Content: Table or Grid View */}
      {filteredRequests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <RiCake2Line size={32} />
          </div>
          <h3 className="text-lg font-heading font-bold text-primaryTextColor">No Party Requests Found</h3>
          <p className="text-gray-500 text-xs sm:text-sm mt-1 max-w-sm mx-auto font-subheading">
            No party requests match your current search and payment/status filters. Try adjusting your selections.
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
              setPaymentFilter("all");
              setPackageFilter("all");
              setSportFilter("all");
            }}
            className="mt-4 bg-primaryBtnColor text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-primaryBtnHoverColor transition-colors cursor-pointer"
          >
            Clear All Filters
          </button>
        </div>
      ) : viewMode === "table" ? (
        /* TABLE VIEW */
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-500 font-heading text-[11px] uppercase tracking-wider">
                  <th className="py-3.5 px-4 font-bold">Booking Ref</th>
                  <th className="py-3.5 px-4 font-bold">Customer & Contact</th>
                  <th className="py-3.5 px-4 font-bold">Package & Sport</th>
                  <th className="py-3.5 px-4 font-bold">Squad Size & Price</th>
                  <th className="py-3.5 px-4 font-bold text-center">Payment Status & Amount</th>
                  <th className="py-3.5 px-4 font-bold text-center">Booking Status</th>
                  <th className="py-3.5 px-4 font-bold text-center">Waiver</th>
                  <th className="py-3.5 px-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-subheading">
                {paginatedRequests.map((req) => {
                  const bookingStatus = getBookingStatusBadge(req.status);
                  const paymentStatus = getPaymentBadge(req.paymentStatus || "unpaid");
                  const pkgBadgeColor = getPackageBadgeColor(req.packageDetails?.packageName);
                  const squadPricing = calculateSquadPricing(
                    req.packageDetails?.packageName,
                    req.packageDetails?.participantsCount,
                    req.totalAmount
                  );

                  return (
                    <tr
                      key={req._id}
                      className="hover:bg-gray-50/80 transition-colors duration-150 group cursor-pointer"
                      onClick={() => handleOpenDetailModal(req)}
                    >
                      {/* 1. Reference & Submitted */}
                      <td className="py-4 px-4 align-top">
                        <div className="font-heading font-bold text-primaryBtnColor group-hover:text-primaryBtnHoverColor transition-colors">
                          {req.bookingReference}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
                          <RiCalendarLine size={11} />
                          {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : "Recent"}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                          <Clock size={10} className="text-gray-400" />
                          <span>{req.packageDetails?.timeSlot || "Afternoon"}</span>
                        </div>
                      </td>

                      {/* 2. Customer & Contact */}
                      <td className="py-4 px-4 align-top max-w-[190px]">
                        <div className="font-semibold text-primaryTextColor text-sm truncate">
                          {req.primarySignee?.firstName} {req.primarySignee?.lastName}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                          <RiMailLine size={12} className="text-gray-400 shrink-0" />
                          <span className="truncate">{req.primarySignee?.email}</span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <RiPhoneLine size={12} className="text-gray-400 shrink-0" />
                          <span>{req.primarySignee?.mobilePhone}</span>
                        </div>
                        {Array.isArray(req.dependants) && req.dependants.length > 0 && (
                          <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-100">
                            <Users size={10} />
                            <span>+{req.dependants.length} Dependant{req.dependants.length > 1 ? "s" : ""}</span>
                          </div>
                        )}
                      </td>

                      {/* 3. Package & Sport */}
                      <td className="py-4 px-4 align-top">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${pkgBadgeColor}`}
                        >
                          {req.packageDetails?.packageName || "Party Package"}
                        </span>
                        <div className="text-xs text-gray-700 font-semibold mt-1.5 flex items-center gap-1">
                          <Trophy size={13} className="text-amber-500" />
                          <span>{req.packageDetails?.sport || "Multi-Sport"}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                          <Calendar size={11} className="text-primaryBtnHoverColor" />
                          <span>
                            {req.packageDetails?.eventDate
                              ? new Date(req.packageDetails.eventDate).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                })
                              : "TBD"}
                          </span>
                        </div>
                      </td>

                      {/* 4. Squad Size & Pricing (Calculated) */}
                      <td className="py-4 px-4 align-top min-w-[150px]">
                        <div className="font-heading font-black text-sm text-gray-800 flex items-center gap-1">
                          <Users size={14} className="text-primaryBtnColor" />
                          <span>{req.packageDetails?.participantsCount || squadPricing.basePlayers} Players</span>
                        </div>

                        {/* Squad breakdown tag */}
                        <div className="text-[11px] text-gray-500 mt-1 leading-tight">
                          Base {squadPricing.basePlayers}p (${squadPricing.basePrice})
                          {squadPricing.extraPlayers > 0 && (
                            <span className="text-primaryBtnHoverColor font-semibold">
                              {" "}
                              + {squadPricing.extraPlayers} extra (${squadPricing.extraCost})
                            </span>
                          )}
                        </div>

                        <div className="text-xs font-black text-primaryBtnColor mt-1">
                          Total: ${squadPricing.finalTotal}
                        </div>
                      </td>

                      {/* 5. Payment Status & Amount (Initially Unpaid - Click to open modal) */}
                      <td className="py-4 px-4 align-top text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleOpenEditModal(req)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border transition-transform hover:scale-105 cursor-pointer shadow-xs ${paymentStatus.bg}`}
                          title="Click to open status & payment modal"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${paymentStatus.dot}`}></span>
                          <span>{paymentStatus.label}</span>
                        </button>

                        {/* Amount ALWAYS showing */}
                        <div className="text-sm font-black text-primaryTextColor font-heading mt-1.5 tracking-tight">
                          ${squadPricing.finalTotal}
                        </div>

                        {req.paymentStatus === "deposit_paid" && (
                          <div className="text-[10px] text-purple-700 font-semibold mt-0.5">
                            ${req.depositAmount || 0} Paid • ${Math.max(0, squadPricing.finalTotal - (req.depositAmount || 0))} Due
                          </div>
                        )}
                        {req.paymentStatus === "paid" && (
                          <div className="text-[10px] text-emerald-600 font-semibold mt-0.5 flex items-center justify-center gap-0.5">
                            <RiCheckLine size={12} /> Settled in full
                          </div>
                        )}
                        {(!req.paymentStatus || req.paymentStatus === "unpaid") && (
                          <div className="text-[10px] text-rose-600 font-semibold mt-0.5">
                            Full ${squadPricing.finalTotal} Due
                          </div>
                        )}
                        {req.paymentStatus === "refunded" && (
                          <div className="text-[10px] text-gray-500 font-semibold mt-0.5">
                            Refunded
                          </div>
                        )}
                      </td>

                      {/* 6. Booking Status */}
                      <td className="py-4 px-4 align-top text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleOpenEditModal(req)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-transform hover:scale-105 cursor-pointer shadow-xs ${bookingStatus.bg}`}
                          title="Click to edit status and details"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${bookingStatus.dot}`}></span>
                          <span>{bookingStatus.label}</span>
                        </button>
                      </td>

                      {/* 7. Waiver / Signature */}
                      <td className="py-4 px-4 align-top text-center">
                        {req.legalConsent?.agreedWaiver ? (
                          <div className="inline-flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 text-[10px] font-semibold">
                            <FileSignature size={11} />
                            <span>Signed</span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 text-[10px] font-semibold">
                            <AlertCircle size={11} />
                            <span>Pending</span>
                          </div>
                        )}
                      </td>

                      {/* 8. Actions */}
                      <td className="py-4 px-4 align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View Full Details */}
                          <button
                            onClick={() => handleOpenDetailModal(req)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            title="View Full Booking Details"
                          >
                            <RiEyeLine size={16} />
                          </button>

                          {/* Quick Status / Edit */}
                          <button
                            onClick={() => handleOpenEditModal(req)}
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                            title="Edit Payment, Status & Financials"
                          >
                            <RiEditLine size={16} />
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteRequest(req._id, req.bookingReference)}
                            className="p-1.5 text-gray-500 hover:text-primaryBtnHoverColor hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            title="Delete Request"
                          >
                            <RiDeleteBinLine size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer with Pagination */}
          <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div>
              Showing <span className="font-bold text-gray-800">{paginatedRequests.length}</span> of{" "}
              <span className="font-bold text-gray-800">{filteredRequests.length}</span> party requests
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <RiArrowLeftSLine size={16} />
              </button>
              <span className="font-semibold text-gray-700 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <RiArrowRightSLine size={16} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* CARD GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {paginatedRequests.map((req) => {
            const bookingStatus = getBookingStatusBadge(req.status);
            const paymentStatus = getPaymentBadge(req.paymentStatus || "unpaid");
            const pkgBadgeColor = getPackageBadgeColor(req.packageDetails?.packageName);
            const squadPricing = calculateSquadPricing(
              req.packageDetails?.packageName,
              req.packageDetails?.participantsCount,
              req.totalAmount
            );

            return (
              <div
                key={req._id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-5 flex flex-col justify-between cursor-pointer group"
                onClick={() => handleOpenDetailModal(req)}
              >
                {/* Top Section: Ref & Status Badges */}
                <div>
                  <div className="flex items-center justify-between gap-2 pb-3 border-b border-gray-100">
                    <span className="font-heading font-black text-sm text-primaryBtnColor group-hover:text-primaryBtnHoverColor transition-colors">
                      {req.bookingReference}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(req);
                        }}
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border hover:scale-105 transition-transform cursor-pointer ${paymentStatus.bg}`}
                        title="Click to update payment & booking status"
                      >
                        {paymentStatus.label}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(req);
                        }}
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border hover:scale-105 transition-transform cursor-pointer ${bookingStatus.bg}`}
                        title="Click to edit status"
                      >
                        {bookingStatus.label}
                      </button>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mt-3">
                    <h4 className="font-bold text-primaryTextColor text-base group-hover:text-primaryBtnColor transition-colors">
                      {req.primarySignee?.firstName} {req.primarySignee?.lastName}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{req.primarySignee?.email}</p>
                    <p className="text-xs text-gray-500">{req.primarySignee?.mobilePhone}</p>
                  </div>

                  {/* Package & Squad Pricing Box */}
                  <div className="mt-4 bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Tier:</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${pkgBadgeColor}`}>
                        {req.packageDetails?.packageName}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Squad Size:</span>
                      <span className="font-bold text-gray-800">
                        {req.packageDetails?.participantsCount || squadPricing.basePlayers} players
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Pricing Breakdown:</span>
                      <span className="font-medium text-gray-600">
                        ${squadPricing.basePrice} base
                        {squadPricing.extraPlayers > 0 ? ` + ${squadPricing.extraPlayers} extra ($${squadPricing.extraCost})` : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-gray-200/60 font-bold">
                      <span className="text-gray-700">Total Quoted:</span>
                      <span className="text-primaryBtnHoverColor text-sm font-black">${squadPricing.finalTotal}</span>
                    </div>
                  </div>

                  {/* Celebrant / Guest of Honor tag */}
                  {req.packageDetails?.celebrantName && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-primaryBtnHoverColor font-semibold">
                      <RiCake2Line size={14} />
                      <span>For {req.packageDetails.celebrantName} (Age {req.packageDetails.celebrantAge || "—"})</span>
                    </div>
                  )}
                </div>

                {/* Bottom Action Footer */}
                <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditModal(req);
                    }}
                    className="text-xs font-bold text-primaryBtnColor hover:text-primaryBtnHoverColor hover:underline flex items-center gap-1.5 cursor-pointer"
                    title="Click to update payment & status"
                  >
                    <CreditCard size={14} />
                    <span>${squadPricing.finalTotal} • Update Status</span>
                  </button>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenDetailModal(req)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => handleOpenEditModal(req)}
                      className="p-1.5 text-gray-500 hover:text-amber-600 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                      title="Edit Status"
                    >
                      <RiEditLine size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteRequest(req._id, req.bookingReference)}
                      className="p-1.5 text-gray-500 hover:text-primaryBtnHoverColor rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                      title="Delete"
                    >
                      <RiDeleteBinLine size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. FULL DETAIL MODAL */}
      {isDetailModalOpen && selectedRequest && (
        <ModalWrapper
          title={`Party Booking Request: ${selectedRequest.bookingReference}`}
          onClose={() => setIsDetailModalOpen(false)}
          maxWidth="max-w-4xl"
        >
          {(() => {
            const pricing = calculateSquadPricing(
              selectedRequest.packageDetails?.packageName,
              selectedRequest.packageDetails?.participantsCount,
              selectedRequest.totalAmount
            );
            const currentPayment = getPaymentBadge(selectedRequest.paymentStatus || "unpaid");
            const currentStatus = getBookingStatusBadge(selectedRequest.status || "pending");
            const balanceDue = Math.max(0, pricing.finalTotal - (selectedRequest.depositAmount || 0));

            return (
              <div className="space-y-6 font-subheading text-xs sm:text-sm text-gray-800">
                {/* Modal Top Banner with Status & Reference */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                      Booking Reference
                    </span>
                    <span className="font-heading font-black text-xl text-primaryBtnColor">
                      {selectedRequest.bookingReference}
                    </span>
                    <span className="text-xs text-gray-500 block mt-0.5">
                      Submitted on {selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString() : "Recently"}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${currentPayment.bg}`}>
                      Payment: {currentPayment.label}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${currentStatus.bg}`}>
                      Status: {currentStatus.label}
                    </span>
                    <button
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleOpenEditModal(selectedRequest);
                      }}
                      className="bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1 shadow-sm transition-colors cursor-pointer ml-1"
                    >
                      <RiEditLine size={14} />
                      <span>Change Status / Payment</span>
                    </button>
                  </div>
                </div>

                {/* Squad Pricing & Financial Breakdown Card */}
                <div className="bg-gradient-to-br from-red-50/40 via-white to-gray-50 p-5 rounded-xl border border-red-100 shadow-xs">
                  <div className="flex items-center justify-between pb-3 border-b border-red-100">
                    <div className="flex items-center gap-2">
                      <Receipt className="text-primaryBtnHoverColor" size={18} />
                      <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-primaryTextColor">
                        Squad Size & Pricing Breakdown
                      </h3>
                    </div>
                    <span className="text-xs font-bold text-primaryBtnColor">
                      Tier: {pricing.tierLabel}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-3 text-xs">
                    <div className="p-3 bg-white rounded-xl border border-gray-200">
                      <span className="text-gray-500 block text-[11px]">Base Squad Limit</span>
                      <div className="font-bold text-gray-800 text-sm mt-0.5">
                        Up to {pricing.basePlayers} Players
                      </div>
                      <span className="text-gray-400 text-[10px] block mt-0.5">
                        Base Rate: ${pricing.basePrice}
                      </span>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200">
                      <span className="text-gray-500 block text-[11px]">Requested Players</span>
                      <div className="font-bold text-primaryBtnColor text-sm mt-0.5">
                        {selectedRequest.packageDetails?.participantsCount || pricing.basePlayers} Players
                      </div>
                      <span className="text-primaryBtnHoverColor text-[10px] font-semibold block mt-0.5">
                        {pricing.extraPlayers > 0 ? `+${pricing.extraPlayers} Extra @ $${pricing.extraPlayerRate}/ea (+$${pricing.extraCost})` : "Within base capacity"}
                      </span>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200">
                      <span className="text-gray-500 block text-[11px]">Total Calculated</span>
                      <div className="font-black text-primaryBtnHoverColor text-base mt-0.5">
                        ${pricing.finalTotal}
                      </div>
                      <span className="text-gray-400 text-[10px] block mt-0.5">
                        Deposit Paid: ${selectedRequest.depositAmount || 0}
                      </span>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200">
                      <span className="text-gray-500 block text-[11px]">Balance Remaining</span>
                      <div className="font-black text-rose-600 text-base mt-0.5">
                        ${balanceDue}
                      </div>
                      <span className="text-gray-500 text-[10px] font-medium block mt-0.5">
                        {selectedRequest.paymentStatus === "paid" ? "Fully Paid" : "Due on arrival"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Grid 2 Columns: Event Package & Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Box 1: Package & Event Details */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <Trophy className="text-primaryBtnHoverColor" size={18} />
                      <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-primaryTextColor">
                        Package & Event Configuration
                      </h3>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Selected Package:</span>
                        <span className="font-bold text-primaryBtnColor">
                          {selectedRequest.packageDetails?.packageName}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Selected Sport:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest.packageDetails?.sport}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Event Date:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest.packageDetails?.eventDate
                            ? new Date(selectedRequest.packageDetails.eventDate).toLocaleDateString("en-US", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "TBD"}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Time Slot:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest.packageDetails?.timeSlot}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Participants:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest.packageDetails?.participantsCount} players
                        </span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-gray-500">Guest of Honor:</span>
                        <span className="font-bold text-primaryBtnHoverColor">
                          {selectedRequest.packageDetails?.celebrantName || "N/A"}{" "}
                          {selectedRequest.packageDetails?.celebrantAge && `(Turning ${selectedRequest.packageDetails.celebrantAge})`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Box 2: Customer Contact Information */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <RiUserLine className="text-primaryBtnColor" size={18} />
                      <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-primaryTextColor">
                        Primary Signee & Contact Info
                      </h3>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Full Name:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest.primarySignee?.firstName} {selectedRequest.primarySignee?.lastName}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Email:</span>
                        <a
                          href={`mailto:${selectedRequest.primarySignee?.email}`}
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          {selectedRequest.primarySignee?.email}
                        </a>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Mobile Phone:</span>
                        <a
                          href={`tel:${selectedRequest.primarySignee?.mobilePhone}`}
                          className="font-semibold text-gray-800"
                        >
                          {selectedRequest.primarySignee?.mobilePhone}
                        </a>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Date of Birth:</span>
                        <span className="font-medium text-gray-700">
                          {selectedRequest.primarySignee?.dob || "Not provided"}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Address:</span>
                        <span className="font-medium text-gray-700 text-right">
                          {selectedRequest.primarySignee?.addressLine1}{" "}
                          {selectedRequest.primarySignee?.city ? `, ${selectedRequest.primarySignee.city}, ${selectedRequest.primarySignee.state} ${selectedRequest.primarySignee.zipCode}` : ""}
                        </span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-gray-500">Signee Type:</span>
                        <span className="font-semibold text-purple-700 capitalize">
                          {selectedRequest.signeeType === "adult_with_dependants"
                            ? "Adult with Dependants"
                            : "Adult (Solo)"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Box 3: Dependants List (if any) */}
                {Array.isArray(selectedRequest.dependants) && selectedRequest.dependants.length > 0 && (
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                      <Users className="text-blue-600" size={18} />
                      <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-primaryTextColor">
                        Registered Dependants ({selectedRequest.dependants.length})
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {selectedRequest.dependants.map((dep, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
                          <div className="font-bold text-gray-800">
                            {dep.firstName} {dep.lastName}
                          </div>
                          <div className="text-gray-500 text-[11px] mt-0.5">
                            DOB: {dep.dob || "N/A"} {dep.city ? `• ${dep.city}, ${dep.state}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Box 4: Custom Add-Ons, Food & Notes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Add-ons & Food */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs space-y-4">
                    <div>
                      <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                        <PlusCircle size={14} className="text-primaryBtnColor" />
                        Selected Add-ons ({Array.isArray(selectedRequest.packageDetails?.addons) ? selectedRequest.packageDetails.addons.length : 0})
                      </h4>
                      {Array.isArray(selectedRequest.packageDetails?.addons) && selectedRequest.packageDetails.addons.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedRequest.packageDetails.addons.map((add, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-1 rounded-lg bg-red-50 text-primaryBtnHoverColor text-xs font-semibold border border-red-100"
                            >
                              + {add}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No add-ons requested.</p>
                      )}
                    </div>

                    <div className="pt-3 border-t border-gray-100">
                      <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                        <Utensils size={14} className="text-amber-600" />
                        Food & Beverages Selections ({Array.isArray(selectedRequest.packageDetails?.foodSelections) ? selectedRequest.packageDetails.foodSelections.length : 0})
                      </h4>
                      {Array.isArray(selectedRequest.packageDetails?.foodSelections) && selectedRequest.packageDetails.foodSelections.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedRequest.packageDetails.foodSelections.map((food, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 text-xs font-semibold border border-amber-100"
                            >
                              🍕 {food}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No catering selections made.</p>
                      )}
                    </div>
                  </div>

                  {/* Special Instructions & Notes */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
                    <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                      <RiFileTextLine size={14} className="text-gray-600" />
                      Client Special Instructions & Notes
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 text-xs text-gray-700 min-h-[90px] leading-relaxed">
                      {selectedRequest.packageDetails?.notes || (
                        <span className="text-gray-400 italic">No special instructions provided by customer.</span>
                      )}
                    </div>

                    {selectedRequest.adminNotes && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <h5 className="font-heading font-bold text-[11px] uppercase tracking-wider text-amber-700 mb-1">
                          Internal Admin Notes:
                        </h5>
                        <p className="text-xs text-gray-700 bg-amber-50/70 p-2.5 rounded-lg border border-amber-200">
                          {selectedRequest.adminNotes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Box 5: Legal Consent & Signed Waiver Verification */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <FileSignature className="text-emerald-600" size={18} />
                      <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-primaryTextColor">
                        Digital Waiver & Electronic Signature
                      </h3>
                    </div>
                    <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                      <RiShieldCheckLine size={15} /> Verified Online Submission
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-xs">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                      <span>Terms & Conditions Agreed</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                      <span>Liability Waiver Accepted</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                      <span>Electronic Consent Verified</span>
                    </div>
                  </div>

                  {/* Signature Canvas Visual Preview Box */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Electronic Signature On File
                    </div>
                    <div className="h-20 bg-white rounded-lg border border-gray-200 flex items-center justify-center p-2 relative overflow-hidden">
                      <svg className="w-48 h-12 text-primaryBtnColor" viewBox="0 0 200 60">
                        <path
                          d={selectedRequest.legalConsent?.signatureData || "M10 35 Q 50 10 90 35 T 160 25"}
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute bottom-1 right-2 text-[10px] text-gray-400 font-mono">
                        Signed by: {selectedRequest.primarySignee?.firstName} {selectedRequest.primarySignee?.lastName} • IP: {selectedRequest.legalConsent?.ipAddress || "Verified"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Bottom Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => handleDeleteRequest(selectedRequest._id, selectedRequest.bookingReference)}
                    className="text-primaryBtnHoverColor hover:bg-red-50 px-3 py-2 rounded-xl font-semibold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <RiDeleteBinLine size={15} />
                    <span>Delete Record</span>
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsDetailModalOpen(false)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleOpenEditModal(selectedRequest);
                      }}
                      className="bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                    >
                      <RiEditLine size={15} />
                      <span>Update Status & Financials</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </ModalWrapper>
      )}

      {/* 6. EDIT STATUS & PAYMENT MODAL */}
      {isEditModalOpen && editingRequest && (
        <ModalWrapper
          title={`Update Status & Financials: ${editingRequest.bookingReference}`}
          onClose={() => setIsEditModalOpen(false)}
          maxWidth="max-w-lg"
        >
          {(() => {
            const pricing = calculateSquadPricing(
              editingRequest.packageDetails?.packageName,
              editingRequest.packageDetails?.participantsCount
            );
            const balanceDue = Math.max(0, Number(statusForm.totalAmount) - Number(statusForm.depositAmount));

            return (
              <form onSubmit={handleSaveStatus} className="space-y-4 font-subheading text-xs sm:text-sm">
                {/* Booking Summary Box with Squad Size Breakdown */}
                <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-800 text-sm">
                      {editingRequest.primarySignee?.firstName} {editingRequest.primarySignee?.lastName}
                    </span>
                    <span className="font-heading font-black text-xs text-primaryBtnHoverColor">
                      {editingRequest.packageDetails?.participantsCount || pricing.basePlayers} Players
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {editingRequest.packageDetails?.packageName} • {editingRequest.packageDetails?.sport}
                  </div>
                  <div className="text-[11px] text-gray-600 bg-white p-2 rounded-lg border border-gray-100 flex items-center justify-between mt-1">
                    <span>
                      Squad Base ({pricing.basePlayers}p @ ${pricing.basePrice}) + {pricing.extraPlayers} extra (${pricing.extraCost})
                    </span>
                    <button
                      type="button"
                      onClick={() => setStatusForm({ ...statusForm, totalAmount: pricing.calculatedTotal })}
                      className="text-primaryBtnColor font-bold hover:underline"
                      title="Reset total amount to squad size calculation"
                    >
                      Use ${pricing.calculatedTotal}
                    </button>
                  </div>
                </div>

                {/* Dropdowns Row: Payment Status & Booking Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Payment Status Dropdown (Initially Unpaid) */}
                  <div>
                    <label className="block font-bold text-gray-700 mb-1 flex items-center gap-1">
                      <CreditCard size={14} className="text-primaryBtnHoverColor" />
                      Payment Status
                    </label>
                    <select
                      value={statusForm.paymentStatus}
                      onChange={(e) => {
                        const newPay = e.target.value;
                        let newDeposit = statusForm.depositAmount;
                        if (newPay === "paid") {
                          newDeposit = statusForm.totalAmount;
                        } else if (newPay === "unpaid") {
                          newDeposit = 0;
                        }
                        setStatusForm({
                          ...statusForm,
                          paymentStatus: newPay,
                          depositAmount: newDeposit,
                        });
                      }}
                      className="w-full p-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30"
                    >
                      <option value="unpaid">Unpaid (Initial)</option>
                      <option value="deposit_paid">Deposit Paid</option>
                      <option value="paid">Paid in Full</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>

                  {/* Booking Status Dropdown */}
                  <div>
                    <label className="block font-bold text-gray-700 mb-1 flex items-center gap-1">
                      <Clock size={14} className="text-primaryBtnColor" />
                      Booking Status
                    </label>
                    <select
                      value={statusForm.status}
                      onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                      className="w-full p-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30"
                    >
                      <option value="pending">Pending Review</option>
                      <option value="contacted">In Review / Contacted</option>
                      <option value="confirmed">Confirmed & Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Financials Row: Deposit & Total Amount */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Deposit Received ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={statusForm.depositAmount}
                      onChange={(e) => {
                        const dep = Number(e.target.value);
                        let pay = statusForm.paymentStatus;
                        if (dep >= statusForm.totalAmount && statusForm.totalAmount > 0) {
                          pay = "paid";
                        } else if (dep > 0) {
                          pay = "deposit_paid";
                        } else {
                          pay = "unpaid";
                        }
                        setStatusForm({
                          ...statusForm,
                          depositAmount: e.target.value,
                          paymentStatus: pay,
                        });
                      }}
                      className="w-full p-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Total Squad Quote ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={statusForm.totalAmount}
                      onChange={(e) => setStatusForm({ ...statusForm, totalAmount: e.target.value })}
                      className="w-full p-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30"
                    />
                  </div>
                </div>

                {/* Live Balance Due Indicator */}
                <div className="p-2.5 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-between text-xs">
                  <span className="font-semibold text-gray-600">Remaining Balance Due:</span>
                  <span className={`font-black text-sm ${balanceDue > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    ${balanceDue}
                  </span>
                </div>

                {/* Internal Admin Notes */}
                <div>
                  <label className="block font-bold text-gray-700 mb-1">
                    Staff / Internal Notes
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Add notes on payment method, court assignments, party host instructions, etc..."
                    value={statusForm.adminNotes}
                    onChange={(e) => setStatusForm({ ...statusForm, adminNotes: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm font-normal text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryBtnHoverColor/30"
                  />
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingStatus}
                    className="px-4 py-2 bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white rounded-xl text-xs font-semibold cursor-pointer transition-colors flex items-center gap-1.5 shadow-sm"
                  >
                    {savingStatus ? (
                      <>
                        <RiRefreshLine className="animate-spin" size={14} />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save Changes</span>
                    )}
                  </button>
                </div>
              </form>
            );
          })()}
        </ModalWrapper>
      )}
    </div>
  );
};

export default AdminPartyRequests;
